import { useState } from 'react';
import axios from 'axios';
import { toast } from 'sonner';
import { Copy, Check } from 'lucide-react';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

/**
 * CopyWatchlistButton — Round 84 (Feb 14, 2026).
 *
 * One-click clone of another user's published watchlist into the
 * caller's own community_public_watchlists. Source `copy_count`
 * increments so the most-copied lists become discoverable.
 */
export default function CopyWatchlistButton({ watchlistId, onCopied }) {
  const [pending, setPending] = useState(false);
  const [done, setDone] = useState(false);

  const handle = async (e) => {
    e.preventDefault();
    e.stopPropagation();
    if (pending || done) return;
    setPending(true);
    try {
      const r = await axios.post(
        `${API}/community/watchlists/${watchlistId}/copy`,
        {},
        { withCredentials: true },
      );
      setDone(true);
      toast.success(`Copied — saved as "${r.data?.name}".`);
      if (onCopied) onCopied(r.data);
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Copy failed.');
    } finally {
      setPending(false);
    }
  };

  return (
    <button
      type="button"
      onClick={handle}
      disabled={pending || done}
      className={`inline-flex items-center gap-1 px-2 py-0.5 rounded text-[10px] font-bold uppercase tracking-wide transition border ${
        done
          ? 'bg-emerald-100 dark:bg-emerald-950/40 text-emerald-700 dark:text-emerald-300 border-emerald-300/60 dark:border-emerald-700/60'
          : 'bg-violet-100 dark:bg-violet-950/40 text-violet-700 dark:text-violet-300 border-violet-300/60 dark:border-violet-700/60 hover:bg-violet-200 dark:hover:bg-violet-950/60'
      } ${pending ? 'opacity-50' : ''}`}
      data-testid={`copy-watchlist-btn-${watchlistId}`}
      title="Copy this watchlist into your own"
    >
      {done ? <Check className="w-3 h-3" /> : <Copy className="w-3 h-3" />}
      {done ? 'Copied' : 'Copy'}
    </button>
  );
}
